import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useExportChargingHistoryReportMutation } from "../../../../../redux/api/ev-charger/evChargerAPI";
import { ChargingSessionGraphViewType } from "../../../../../shared/oversight-core/enums/charging-session-graph-view-type";
import { IHttpError } from "../../../../../shared/oversight-core/interfaces/http-errror";
import AppDatePicker from "../../../../../shared/oversight-core/ui-elements/app-date-picker/app-date-picker";
import AppSelect, {
  Option,
} from "../../../../../shared/oversight-core/ui-elements/app-select/app-select";
import AppTabs, {
  TabComponentInterface,
} from "../../../../../shared/oversight-core/ui-elements/app-tabs/app-tabs";
import ModalContainer from "../../../../../shared/oversight-core/ui-elements/modal-container/modal-container";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../../../shared/oversight-core/utils/toast";

const timeTabs: TabComponentInterface[] = [
  { id: 1, buttonName: "Daily" },
  { id: 2, buttonName: "Monthly" },
  { id: 3, buttonName: "Yearly" },
];

const formatOptions: Option[] = [
  { value: "PDF", label: "PDF" },
  { value: "CSV", label: "CSV" },
  { value: "XLSX", label: "XLSX" },
];

const DEFAULT_TAB_ID = 1;
const DEFAULT_FORMAT = "PDF";

/** Default From/To range for a given timeframe tab. From always falls in an
 * earlier period than To, so the defaults pass range validation. Computed
 * each time the modal opens, so it follows the current date. */
const getDefaultRange = (tabId: number) => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  if (tabId === 2) {
    return { from: new Date(month === 0 ? year - 1 : year, 0, 1), to: now };
  }
  if (tabId === 3) {
    return { from: new Date(year - 6, 0, 1), to: now };
  }
  // daily: the full current calendar month (day 0 of next month = last day)
  return { from: new Date(year, month, 1), to: new Date(year, month + 1, 0) };
};

/** Comparable index of the period a date falls in for the given tab, so that
 * two dates in the same day / month / year compare equal. */
const getPeriodIndex = (date: Date, tabId: number) => {
  if (tabId === 3) return date.getFullYear();
  if (tabId === 2) return date.getFullYear() * 12 + date.getMonth();
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  ).getTime();
};

const periodLabels: Record<number, string> = {
  1: "date",
  2: "month",
  3: "year",
};

/** Validation message for the selected range, or undefined if it is valid. */
const getRangeError = (from: Date, to: Date, tabId: number) => {
  const fromIndex = getPeriodIndex(from, tabId);
  const toIndex = getPeriodIndex(to, tabId);
  const period = periodLabels[tabId] ?? periodLabels[1];
  if (fromIndex > toIndex) {
    return `'To' ${period} cannot be before 'From' ${period}`;
  }
  if (fromIndex === toIndex) {
    return `'To' ${period} must be after 'From' ${period}`;
  }
  return undefined;
};

interface IProps {
  show: boolean;
  onClose: () => void;
  onCancel?: () => void;
  evChargerId?: string;
  idTag?: string;
}

const ExportChargingHistoryModal = (props: IProps) => {
  const { show, onClose, onCancel, evChargerId, idTag } = props;

  const [selectedTimeTabId, setSelectedTimeTabId] =
    useState<number>(DEFAULT_TAB_ID);
  const [fromDate, setFromDate] = useState<Date>(
    () => getDefaultRange(DEFAULT_TAB_ID).from,
  );
  const [toDate, setToDate] = useState<Date>(
    () => getDefaultRange(DEFAULT_TAB_ID).to,
  );
  const [format, setFormat] = useState<string>(DEFAULT_FORMAT);

  // The modal stays mounted while hidden, so its state would otherwise survive
  // a cancel/close. Restore the defaults every time it is opened.
  useEffect(() => {
    if (!show) return;
    const { from, to } = getDefaultRange(DEFAULT_TAB_ID);
    setSelectedTimeTabId(DEFAULT_TAB_ID);
    setFromDate(from);
    setToDate(to);
    setFormat(DEFAULT_FORMAT);
  }, [show]);

  const [triggerExport, { isLoading }] =
    useExportChargingHistoryReportMutation();

  const handleTimeTabChange = (tabId: number) => {
    const { from, to } = getDefaultRange(tabId);
    setSelectedTimeTabId(tabId);
    setFromDate(from);
    setToDate(to);
  };

  const monthYearPicker = selectedTimeTabId === 2;
  const yearPicker = selectedTimeTabId === 3;
  const inputDateFormat =
    selectedTimeTabId === 1
      ? "dd MMM yyyy"
      : selectedTimeTabId === 2
        ? "MMMM yyyy"
        : "yyyy";

  const rangeError =
    fromDate && toDate
      ? getRangeError(fromDate, toDate, selectedTimeTabId)
      : undefined;

  const getViewType = (): ChargingSessionGraphViewType => {
    switch (selectedTimeTabId) {
      case 2:
        return ChargingSessionGraphViewType.MONTH;
      case 3:
        return ChargingSessionGraphViewType.YEAR;
      default:
        return ChargingSessionGraphViewType.DAY;
    }
  };

  const handleExport = async () => {
    if (!fromDate || !toDate) {
      showErrorMessage("Please select valid From and To dates");
      return;
    }

    // the button is disabled while the range is invalid; this is a safeguard
    if (rangeError) {
      showErrorMessage(rangeError);
      return;
    }

    const start = new Date(fromDate);
    start.setHours(0, 0, 0, 0);

    const end = new Date(toDate);
    end.setHours(23, 59, 59, 999);

    try {
      const blob = await triggerExport({
        evChargerId,
        idTag,
        startDate: start.toISOString(),
        endDate: end.toISOString(),
        viewType: getViewType(),
        zoneOffset: "Asia/Colombo",
        format,
      }).unwrap();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const extension = format.toLowerCase();
      link.setAttribute(
        "download",
        `charging-history-${new Date().getTime()}.${extension}`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showSuccessMessage(`Charging history report exported as ${format}`);
      if (onClose) onClose();
    } catch (err) {
      const httpError = err as IHttpError;
      showErrorMessage(
        httpError?.message || "Failed to export charging history report",
      );
    }
  };

  return (
    <ModalContainer
      show={show}
      onClose={onClose}
      onCancel={onCancel || onClose}
      onConfirm={handleExport}
      isLoading={isLoading}
      disabled={!!rangeError}
      title={
        <div>
          <div className="font-size-24 font-weight-500 text-dark">
            Export Charging History
          </div>
          <div className="font-size-16 font-weight-400 text-light mt-1">
            Select time frame and export report.
          </div>
        </div>
      }
      confirmButtonText="Export Report"
      size="modal-md"
    >
      <div className="mt-3">
        <div className="mb-4">
          <label className="font-size-14 font-weight-500 text-dark mb-2 d-block">
            Time
          </label>
          <AppTabs
            buttons={timeTabs}
            onChange={handleTimeTabChange}
            selectedTabId={selectedTimeTabId}
          />
        </div>

        <Row className="g-3">
          <Col xs={12} sm={6}>
            <label className="font-size-14 font-weight-500 text-dark mb-2 d-block">
              From
            </label>
            <AppDatePicker
              selectedDate={fromDate}
              onChange={(d) => setFromDate(d)}
              isInput={true}
              inputDateFormat={inputDateFormat}
              monthYearPicker={monthYearPicker}
              yearPicker={yearPicker}
            />
          </Col>
          <Col xs={12} sm={6}>
            <label className="font-size-14 font-weight-500 text-dark mb-2 d-block">
              To
            </label>
            <AppDatePicker
              selectedDate={toDate}
              onChange={(d) => setToDate(d)}
              isInput={true}
              inputDateFormat={inputDateFormat}
              monthYearPicker={monthYearPicker}
              yearPicker={yearPicker}
              error={rangeError}
            />
          </Col>
        </Row>

        <AppSelect
          className="mt-4"
          label="Format"
          options={formatOptions}
          selectedValue={formatOptions.find((opt) => opt.value === format)}
          onChangeOption={(option) => {
            if (option) setFormat(option.value);
          }}
        />
      </div>
    </ModalContainer>
  );
};

export default ExportChargingHistoryModal;
